<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  ArrowRight,
  CircleDollarSign,
  LoaderCircle,
  RefreshCw,
  WalletCards
} from 'lucide-vue-next'
import { api, errorText, formatDate, object, rows, type JsonRecord } from '../api'
import {
  amountError,
  amountForQuantity,
  amountMinor,
  pointText,
  pointUnitsScaled,
  quantityForAmount,
  unitPriceText,
  yuanText,
  type PaymentProduct
} from '../utils/purchaseMath'

interface Order {
  order_id: string
  amount_minor: string
  channel_code: string
  payment_status: string
  fulfillment_status: string
  created_at?: string
  updated_at?: string
}

interface Checkout {
  kind: string
  image?: string
  url?: string
  action?: string
  parameters?: Record<string, string>
}

interface PurchaseReply {
  order: Order
  checkout?: Checkout
  expires_at?: string
}

interface PurchaseAttempt {
  request_id: string
  products_id: string
  quantity: string
  channel_code: 'WECHAT' | 'ALIPAY'
  scene: 'NATIVE' | 'PAGE'
  amount_minor: string
  order_id?: string
}

const loading = ref(false)
const productsLoading = ref(false)
const busy = ref(false)
const creating = ref(false)
const checkingPayment = ref(false)
const pageError = ref('')
const purchaseError = ref('')
const paymentNotice = ref('')
const account = ref<JsonRecord>({})
const transactions = ref<JsonRecord[]>([])
const products = ref<PaymentProduct[]>([])
const selected = ref('')
const amountYuan = ref('100.00')
const channel = ref<'WECHAT' | 'ALIPAY'>('ALIPAY')
const pending = ref<PurchaseAttempt | null>(null)
const order = ref<Order | null>(null)
const checkout = ref<Checkout | null>(null)
const qrVisible = ref(false)
const paymentReturnPending = ref(false)
const expiresAt = ref<string | null>(null)
const now = ref(Date.now())
const presetAmounts = ['100', '1000', '5000', '10000', '50000', '100000']

let clockTimer: ReturnType<typeof setInterval> | undefined
let pollTimer: ReturnType<typeof setInterval> | undefined
let generation = 0
let queryVersion = 0
let pollCount = 0
let pollFailures = 0
let automaticQueryRunning = false
let disposed = false
const fulfillmentTimers = new Map<string, ReturnType<typeof setTimeout>>()

// 账户三值后端已按整数点输出；本地兜底与历史流水仍可能出现尾随零，统一裁掉（不改数值大小）。
function trimPointsText(value: unknown): string {
  if (value == null || value === '') return '—'
  const text = String(value)
  return /\.\d*0$|\.$/.test(text) ? text.replace(/\.?0+$/, '') : text
}
const balance = computed(() => trimPointsText(account.value.balance ?? account.value.points))
const frozen = computed(() => trimPointsText(account.value.frozen))
const available = computed(() => {
  if (account.value.available != null) return trimPointsText(account.value.available)
  if (account.value.balance == null || account.value.frozen == null) return '—'
  const b = Number(account.value.balance)
  const f = Number(account.value.frozen)
  return Number.isFinite(b) && Number.isFinite(f) ? trimPointsText(b - f) : '—'
})
const selectedProduct = computed(() => products.value.find(item => item.id === selected.value))
const formLocked = computed(() => qrVisible.value)
const paid = computed(() => order.value?.payment_status === 'PAID')
const settled = computed(() => order.value?.fulfillment_status === 'SUCCEEDED')
const closed = computed(() => order.value?.payment_status === 'CLOSED')
const needsSupport = computed(() => ['SUSPENDED', 'CANCELED'].includes(order.value?.fulfillment_status || ''))
const paymentLabel = computed(() => (pending.value?.channel_code || channel.value) === 'ALIPAY' ? '支付宝' : '微信')
const quantity = computed(() => quantityForAmount(amountYuan.value, selectedProduct.value))
const selectedTotalPoints = computed(() => {
  const units = quantity.value === null ? null : pointUnitsScaled(quantity.value, selectedProduct.value)
  return units === null ? '—' : pointText(units)
})
const estimatedYuan = computed(() => {
  if (!quantity.value) return '—'
  return amountForQuantity(quantity.value, selectedProduct.value) || '—'
})
const amountHint = computed(() => {
  const invalid = amountError(amountYuan.value)
  if (invalid) return invalid
  if (!selectedProduct.value) return ''
  return quantity.value === null || selectedTotalPoints.value === '—'
    ? `该商品当前价格无法精确换算，请更换整数金额或联系工作人员。单价 ¥${unitPriceText(selectedProduct.value)}/购买单位。`
    : ''
})
const totalYuan = computed(() => {
  if (order.value && /^[0-9]+$/.test(order.value.amount_minor)) return yuanText(BigInt(order.value.amount_minor))
  return estimatedYuan.value
})
const checkoutExpired = computed(() => !!expiresAt.value && Date.parse(expiresAt.value) <= now.value)
const remainingTime = computed(() => {
  if (!expiresAt.value || !Number.isFinite(Date.parse(expiresAt.value))) return ''
  const seconds = Math.max(0, Math.ceil((Date.parse(expiresAt.value) - now.value) / 1000))
  return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`
})
const supportsPayment = computed(() => products.value.length > 0)

function parseProduct(value: JsonRecord): PaymentProduct {
  let params: JsonRecord = {}
  const raw = value.fulfillment_params_json
  if (typeof raw === 'string' && raw) {
    try {
      params = object(JSON.parse(raw))
    } catch {
      params = {}
    }
  }
  return {
    id: String(value.id ?? ''),
    products_name: String(value.products_name ?? '知应点数'),
    unit_price: String(value.unit_price ?? ''),
    points_per_unit: String(params.points_per_unit ?? value.points_per_unit ?? '1')
  }
}

async function loadAccountAndUsage() {
  const results = await Promise.allSettled([api.get('/points'), api.get('/usage')])
  if (results[0].status === 'fulfilled') account.value = object(results[0].value)
  else pageError.value = `账户：${errorText(results[0].reason)}`
  if (results[1].status === 'fulfilled') transactions.value = rows(results[1].value)
  else pageError.value = `${pageError.value ? `${pageError.value}；` : ''}流水：${errorText(results[1].reason)}`
}

async function loadAll() {
  loading.value = true
  pageError.value = ''
  await loadAccountAndUsage()
  loading.value = false
}

async function loadProducts() {
  productsLoading.value = true
  purchaseError.value = ''
  try {
    products.value = rows(await api.get('/payment/products')).map(parseProduct).filter(item => item.id && item.unit_price)
    selected.value = products.value[0]?.id || ''
  } catch (cause) {
    products.value = []
    purchaseError.value = `暂时无法加载购买信息：${errorText(cause)}`
  } finally {
    productsLoading.value = false
  }
}

function isPreset(value: string) {
  return amountMinor(amountYuan.value) === BigInt(value) * 100n
}

function selectPreset(value: string) {
  if (formLocked.value || busy.value) return
  amountYuan.value = `${value}.00`
  purchaseError.value = ''
}

function formatAmount() {
  if (formLocked.value || busy.value) return
  const minor = amountMinor(amountYuan.value)
  if (minor !== null) amountYuan.value = yuanText(minor)
}

function stopPoll() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = undefined
}

function resetCheckout() {
  generation++
  queryVersion++
  stopPoll()
  automaticQueryRunning = false
  pending.value = null
  order.value = null
  checkout.value = null
  qrVisible.value = false
  paymentReturnPending.value = false
  expiresAt.value = null
  busy.value = false
  creating.value = false
  checkingPayment.value = false
  purchaseError.value = ''
  paymentNotice.value = ''
}

function startPoll() {
  stopPoll()
  pollCount = 0
  pollFailures = 0
  if (!order.value || order.value.payment_status !== 'PENDING') return
  pollTimer = setInterval(() => {
    if (++pollCount > 120) {
      stopPoll()
      return
    }
    if (!busy.value) void queryOrder(false, true)
  }, 5000)
}

function followFulfillment(id: string, remaining = 120) {
  if (disposed || remaining <= 0 || fulfillmentTimers.has(id)) return
  const handle = setTimeout(async () => {
    try {
      const result = object(await api.get(`/payment/orders/${encodeURIComponent(id)}`)) as unknown as Order
      if (disposed) return
      if (result.fulfillment_status === 'SUCCEEDED') {
        await loadAccountAndUsage()
        return
      }
      if (['SUSPENDED', 'CANCELED'].includes(result.fulfillment_status)) return
    } catch {
      // A later manual refresh still reads the authoritative order and balance.
    } finally {
      fulfillmentTimers.delete(id)
    }
    followFulfillment(id, remaining - 1)
  }, 5000)
  fulfillmentTimers.set(id, handle)
}

async function receiveOrder(value: Order) {
  order.value = value
  if (value.payment_status === 'PENDING') {
    startPoll()
  } else if (value.payment_status === 'PAID') {
    pending.value = null
    paymentReturnPending.value = false
    paymentNotice.value = ''
    purchaseError.value = ''
    stopPoll()
    checkout.value = null
    qrVisible.value = false
    if (value.fulfillment_status === 'SUCCEEDED') {
      await loadAccountAndUsage()
    } else {
      followFulfillment(value.order_id)
    }
  } else if (value.payment_status === 'CLOSED' || needsSupport.value) {
    pending.value = null
    paymentReturnPending.value = false
    paymentNotice.value = ''
    purchaseError.value = ''
    checkout.value = null
    qrVisible.value = false
    stopPoll()
  }
}

function friendlyError(cause: unknown, fallback: string) {
  const message = errorText(cause)
  if (message.includes('返回了非 JSON')) return fallback
  return message || fallback
}

async function queryOrder(checkChannel = false, automatic = false) {
  if (busy.value || automaticQueryRunning || !order.value) return
  const epoch = generation
  const version = ++queryVersion
  const id = order.value.order_id
  if (automatic) automaticQueryRunning = true
  else {
    busy.value = true
    checkingPayment.value = true
    purchaseError.value = ''
    paymentNotice.value = ''
  }
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 15000)
  try {
    const suffix = checkChannel ? '?refresh=true' : ''
    const value = object(await api.get(`/payment/orders/${encodeURIComponent(id)}${suffix}`, false, {
      signal: controller.signal
    })) as unknown as Order
    if (disposed || epoch !== generation || version !== queryVersion) return
    await receiveOrder(value)
    if (epoch !== generation) return
    if (checkChannel && value.payment_status === 'PENDING') {
      paymentNotice.value = '支付中心尚未确认到账，已继续自动查询；如已完成付款请稍候。'
    }
    pollFailures = 0
  } catch (cause) {
    if (disposed || epoch !== generation || version !== queryVersion) return
    if (!automatic || ++pollFailures >= 3) purchaseError.value = friendlyError(cause, '暂时无法更新支付状态，请稍后重试。')
    if (automatic && pollFailures >= 3) stopPoll()
  } finally {
    clearTimeout(timeout)
    if (epoch === generation && version === queryVersion) {
      if (automatic) automaticQueryRunning = false
      else {
        busy.value = false
        checkingPayment.value = false
      }
    }
  }
}

async function createOrder() {
  if (busy.value || productsLoading.value || disposed) return
  if (!pending.value) {
    const purchaseQuantity = quantity.value
    const minor = amountMinor(amountYuan.value)
    if (!selectedProduct.value || !purchaseQuantity || minor === null || selectedTotalPoints.value === '—') {
      purchaseError.value = amountHint.value || '请调整购买金额后重试。'
      return
    }
    pending.value = {
      request_id: crypto.randomUUID(),
      products_id: selected.value,
      quantity: purchaseQuantity,
      channel_code: channel.value,
      scene: channel.value === 'WECHAT' ? 'NATIVE' : 'PAGE',
      amount_minor: minor.toString()
    }
    order.value = null
  }
  const epoch = generation
  const attempt = pending.value
  queryVersion++
  automaticQueryRunning = false
  stopPoll()
  busy.value = true
  creating.value = true
  purchaseError.value = ''
  paymentNotice.value = ''
  checkout.value = null
  expiresAt.value = null
  try {
    const result = object(await api.post('/payment/orders', {
      requestId: attempt.request_id,
      productsId: attempt.products_id,
      quantity: attempt.quantity,
      channel: attempt.channel_code,
      scene: attempt.scene
    })) as unknown as PurchaseReply
    if (disposed || epoch !== generation) return
    if (!result.order?.order_id || result.order.amount_minor !== attempt.amount_minor
      || (attempt.order_id && attempt.order_id !== result.order.order_id)) {
      purchaseError.value = '返回的购买金额或订单信息不一致，请关闭窗口后重新填写。'
      return
    }
    pending.value = { ...attempt, order_id: result.order.order_id }
    await receiveOrder(result.order)
    if (epoch !== generation) return
    if (result.order.payment_status === 'PENDING') {
      checkout.value = result.checkout || null
      expiresAt.value = result.expires_at || null
      now.value = Date.now()
      if (!checkout.value) purchaseError.value = `${paymentLabel.value}未返回付款入口，请重试。`
      startPoll()
      if (checkout.value) openPayment(checkout.value)
    }
  } catch (cause) {
    if (disposed || epoch !== generation) return
    purchaseError.value = friendlyError(cause, `暂时无法获取${paymentLabel.value}付款入口，请重试。`)
  } finally {
    if (epoch === generation) {
      busy.value = false
      creating.value = false
    }
  }
}

function openPayment(entry: Checkout | null = checkout.value) {
  if (!entry) return
  if (entry.kind === 'QR_CODE' && entry.image) {
    qrVisible.value = true
    return
  }
  if (entry.kind === 'REDIRECT' && entry.url?.startsWith('https://')) {
    window.location.assign(entry.url)
    return
  }
  if (entry.kind !== 'FORM' || !entry.action?.startsWith('https://') || !entry.parameters) return
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = entry.action
  form.target = '_self'
  for (const [name, value] of Object.entries(entry.parameters)) {
    const field = document.createElement('input')
    field.type = 'hidden'
    field.name = name
    field.value = value
    form.append(field)
  }
  document.body.append(form)
  HTMLFormElement.prototype.submit.call(form)
  form.remove()
}

function restoreReturnedOrder() {
  const raw = sessionStorage.getItem('zhiying-payment-return')
  if (!raw) return
  sessionStorage.removeItem('zhiying-payment-return')
  try {
    const value = JSON.parse(raw) as { orderId?: unknown; receivedAt?: unknown }
    const orderId = String(value.orderId ?? '')
    const receivedAt = Number(value.receivedAt ?? 0)
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(orderId) || !Number.isFinite(receivedAt)
      || Date.now() - receivedAt > 30 * 60 * 1000) return
    order.value = {
      order_id: orderId,
      amount_minor: '',
      channel_code: 'ALIPAY',
      payment_status: 'PENDING',
      fulfillment_status: 'PENDING'
    }
    paymentReturnPending.value = true
    void queryOrder(true).finally(() => {
      if (disposed) return
      paymentReturnPending.value = false
      if (order.value?.payment_status === 'PENDING') startPoll()
    })
  } catch {
    // Ignore malformed browser return state and leave the normal recharge page usable.
  }
}

function detail(row: JsonRecord) {
  return object(row.detail)
}

// 支付订单：只展示支付中心入账产生的充值流水（kind=topup），模型消耗等内部流水不再露出。
const purchaseOrders = computed(() => transactions.value.filter(row => String(row.kind ?? detail(row).kind) === 'topup'))

function paidYuan(row: JsonRecord): string {
  const minor = String(detail(row).payment_amount_minor ?? '')
  return /^[0-9]+$/.test(minor) ? yuanText(BigInt(minor)) : '—'
}

function handlePageShow(event: PageTransitionEvent) {
  if (!event.persisted) return
  qrVisible.value = false
  resetCheckout()
  void loadAccountAndUsage()
}

onMounted(async () => {
  clockTimer = setInterval(() => { now.value = Date.now() }, 1000)
  window.addEventListener('pageshow', handlePageShow)
  await Promise.all([loadAll(), loadProducts()])
  restoreReturnedOrder()
})

onUnmounted(() => {
  disposed = true
  window.removeEventListener('pageshow', handlePageShow)
  resetCheckout()
  if (clockTimer) clearInterval(clockTimer)
  for (const handle of fulfillmentTimers.values()) clearTimeout(handle)
  fulfillmentTimers.clear()
})

function closeQr(done: () => void) {
  done()
  resetCheckout()
}

function refreshQr() {
  resetCheckout()
  void createOrder()
}
</script>

<template>
  <div class="page-header">
    <div>
      <h1 class="page-title">点数与用量</h1>
      <p class="page-description">为企业空间购买点数，支付成功后由统一支付中心通知入账。</p>
    </div>
    <div class="page-actions">
      <el-button :icon="RefreshCw" :loading="loading" @click="loadAll">刷新</el-button>
    </div>
  </div>

  <div class="page-content">
    <div v-if="pageError" class="error-banner">{{ pageError }}</div>
    <el-alert v-if="paymentReturnPending" class="return-alert" type="info" :closable="false" show-icon
      title="正在确认刚才的支付结果"
      description="到账后会自动刷新点数，你可以继续使用本页面。" />
    <p v-else-if="paymentNotice" role="status" class="return-notice">{{ paymentNotice }}</p>

    <section class="summary-grid" aria-label="点数账户概览">
      <article class="summary-card">
        <div class="summary-label"><span>账面余额</span><WalletCards :size="17" /></div>
        <strong>{{ loading ? '…' : balance }}</strong>
        <p>企业账户点数</p>
      </article>
      <article class="summary-card">
        <div class="summary-label"><span>冻结额度</span><CircleDollarSign :size="17" /></div>
        <strong>{{ loading ? '…' : frozen }}</strong>
        <p>在途模型调用预留</p>
      </article>
      <article class="summary-card">
        <div class="summary-label"><span>可用额度</span><WalletCards :size="17" /></div>
        <strong>{{ loading ? '…' : available }}</strong>
        <p>账面余额减去冻结额度</p>
      </article>
    </section>

    <section class="panel recharge-panel">
      <div class="purchase-heading">
        <div>
          <div class="heading-line">
            <h2>购买点数</h2>
            <el-tag v-if="selectedProduct" type="success" effect="plain">{{ selectedProduct.products_name }}</el-tag>
          </div>
          <p>选择常用金额或自由输入；最终价格与点数以支付中心订单快照为准。</p>
        </div>
      </div>

      <div v-if="productsLoading" class="loading-state"><LoaderCircle class="spin" :size="22" />正在读取可购买商品…</div>
      <el-alert v-else-if="!supportsPayment" type="warning" :closable="false" show-icon
        title="暂时无法购买"
        :description="purchaseError || '支付中心暂未返回可购买的知应点数商品，请联系工作人员。'" />
      <template v-else>
        <p v-if="purchaseError" role="alert" class="payment-error">{{ purchaseError }}</p>
        <el-form label-position="top" class="purchase-form" @submit.prevent="createOrder">
          <label class="field-label" for="purchase-amount">购买金额</label>
          <div class="amount-presets" role="group" aria-label="常用购买金额">
            <el-button v-for="preset in presetAmounts" :key="preset"
              :aria-pressed="isPreset(preset)" :class="{ 'is-selected': isPreset(preset) }"
              :disabled="formLocked || busy" @click="selectPreset(preset)">¥{{ preset }}</el-button>
          </div>
          <el-input id="purchase-amount" v-model="amountYuan" class="custom-amount"
            :disabled="formLocked || busy" inputmode="decimal" aria-label="购买金额，单位元"
            placeholder="0.01 元起，自由输入金额" @input="purchaseError = ''" @blur="formatAmount">
            <template #prefix>¥</template>
            <template #suffix>元</template>
          </el-input>
          <p v-if="amountHint" class="amount-error" role="status">{{ amountHint }}</p>

          <div class="points-preview" aria-live="polite">
            <span>预计到账</span>
            <strong>{{ selectedTotalPoints }} <small>点</small></strong>
            <span class="unit-price">参考单价 ¥{{ unitPriceText(selectedProduct) }}/购买单位</span>
          </div>

          <div class="payment-methods">
            <span class="field-label">支付方式</span>
            <el-radio-group v-model="channel" :disabled="formLocked || busy" aria-label="支付方式">
              <el-radio value="ALIPAY" border>
                <span class="pay-brand alipay" aria-hidden="true">支</span>支付宝
              </el-radio>
              <el-radio value="WECHAT" border>
                <span class="pay-brand wechat" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9.3 3C4.7 3 1 6 1 9.7c0 2.1 1.2 4 3.1 5.2l-.8 2.4 2.8-1.4c.9.3 2 .5 3.2.5h.6a6.5 6.5 0 0 1-.4-2.2c0-3.5 3.3-6.4 7.4-6.4h.5C16.5 5 13.2 3 9.3 3Zm-2.8 5.6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm5.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
                    <path d="M23 14.2c0-3-2.8-5.4-6.3-5.4s-6.3 2.4-6.3 5.4 2.8 5.4 6.3 5.4c.9 0 1.7-.1 2.4-.4l2.3 1.2-.6-2C22.2 17.4 23 15.9 23 14.2Zm-8.5-.9a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6Zm4.4 0a.8.8 0 1 1 0-1.6.8.8 0 0 1 0 1.6Z" />
                  </svg>
                </span>微信支付
              </el-radio>
            </el-radio-group>
          </div>

          <div class="purchase-submit">
            <div class="purchase-amount">
              <span>实付金额</span>
              <strong><small>¥</small>{{ estimatedYuan }}</strong>
            </div>
            <el-button type="primary" size="large" :loading="creating"
              :disabled="busy || !selected || estimatedYuan === '—' || selectedTotalPoints === '—'"
              @click="createOrder">立即支付<el-icon class="pay-arrow"><ArrowRight /></el-icon></el-button>
          </div>
        </el-form>

        <el-alert v-if="creating" class="payment-inline-state" type="info" :closable="false" show-icon
          title="正在发起支付" description="正在读取支付入口，请稍候；如果是支付宝，完成后会自动返回本页。" />
        <el-alert v-else-if="settled" class="payment-inline-state" type="success" :closable="false" show-icon
          title="支付成功，点数已到账" :description="`订单 ¥${totalYuan} 已完成入账。`" />
        <el-alert v-else-if="closed" class="payment-inline-state" type="error" :closable="false" show-icon
          title="付款入口已失效" description="本次付款入口已关闭，可以调整购买金额后重新发起支付。" />
        <el-alert v-else-if="needsSupport" class="payment-inline-state" type="warning" :closable="false" show-icon
          title="到账需要协助" description="支付已经确认，但点数入账需要工作人员处理。" />
        <el-alert v-else-if="paid" class="payment-inline-state" type="success" :closable="false" show-icon
          title="支付成功，点数正在到账" description="页面会自动刷新到账结果，你可以继续使用本页。" />
      </template>
    </section>

    <el-dialog v-model="qrVisible" class="checkout-dialog" width="440px" align-center destroy-on-close
      :close-on-click-modal="false" :before-close="closeQr">
      <template #header>
        <div class="checkout-panel-head">
          <div>
            <h2>微信扫码支付</h2>
            <p>当前弹窗只用于展示本次订单的微信付款二维码。</p>
          </div>
        </div>
      </template>
      <div class="checkout-dialog-content" aria-live="polite">
        <strong class="checkout-amount">¥{{ totalYuan }}</strong>
        <div v-if="checkout?.image" class="qr-frame" :class="{ expired: checkoutExpired }">
          <img :src="checkout.image" alt="微信扫码支付本订单" width="280" height="280" />
          <div v-if="checkoutExpired" class="qr-expired">
            <span>二维码已过期</span>
            <el-button type="primary" :disabled="busy" @click="refreshQr">刷新二维码</el-button>
          </div>
        </div>
        <template v-if="!checkoutExpired">
          <p>打开微信，扫一扫完成支付</p>
          <p class="checkout-polling">
            <span class="status-dot" />支付后自动确认
            <span v-if="remainingTime"> · {{ remainingTime }} 后失效</span>
          </p>
        </template>
        <div class="checkout-actions">
          <el-button link :disabled="busy" :loading="checkingPayment" @click="queryOrder(true)">我已支付</el-button>
        </div>
      </div>
    </el-dialog>

    <section class="panel usage-panel">
      <div class="panel-head">
        <div><h2>支付订单</h2><p>这里只显示通过支付购买点数的入账记录。</p></div>
      </div>
      <div v-if="loading && !purchaseOrders.length" class="empty-state">正在读取支付订单…</div>
      <div v-else-if="!purchaseOrders.length" class="empty-state">
        <WalletCards :size="22" />
        <strong>暂无支付订单</strong>
        <span>完成一笔支付后，这里会显示对应的点数入账记录。</span>
      </div>
      <div v-else class="table-scroll">
        <table class="data-table">
          <thead><tr><th>时间</th><th>订单号</th><th>支付金额</th><th>到账点数</th><th>到账后余额</th></tr></thead>
          <tbody>
            <tr v-for="txn in purchaseOrders" :key="String(txn.id)">
              <td>{{ formatDate(txn.created_at) }}</td>
              <td class="mono">{{ txn.order_id ?? '—' }}</td>
              <td class="mono">¥{{ paidYuan(txn) }}</td>
              <td class="mono">{{ trimPointsText(detail(txn).delta ?? txn.delta) }}</td>
              <td class="mono">{{ trimPointsText(detail(txn).balance ?? txn.balance) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style scoped>
.summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin-bottom: 20px; }
.summary-card { min-width: 0; padding: 22px 24px; border: 1px solid var(--line); border-radius: 14px; background: var(--paper); box-shadow: none; }
.summary-label { display: flex; align-items: center; justify-content: space-between; color: var(--text); font-size: 13px; }
.summary-card strong { display: block; margin-top: 18px; color: var(--ink); font-size: 28px; line-height: 1.15; font-weight: 650; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.summary-card p { margin: 10px 0 0; color: var(--muted); font-size: 12px; }
.recharge-panel { margin-bottom: 20px; padding: 28px 30px 20px; }
.purchase-heading { margin-bottom: 26px; }
.heading-line { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.purchase-heading h2, .panel-head h2 { margin: 0; color: var(--ink); font-size: 18px; font-weight: 620; }
.purchase-heading p, .panel-head p { margin: 7px 0 0; color: var(--muted); font-size: 13px; line-height: 1.65; }
.purchase-form { max-width: 960px; }
.field-label { display: block; margin-bottom: 12px; color: var(--text); font-size: 14px; }
.amount-presets { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
.amount-presets :deep(.el-button) { width: 100%; height: 44px; margin: 0; padding: 0 8px; border-color: var(--line); border-radius: 9px; background: var(--paper); color: var(--ink); font-size: 15px; }
.amount-presets :deep(.el-button:hover) { background: var(--brand-soft); color: var(--brand); }
.amount-presets :deep(.el-button.is-selected) { color: var(--brand); border-color: var(--brand); background: var(--brand-soft); }
.custom-amount { max-width: 300px; margin-top: 12px; }
.custom-amount :deep(.el-input__wrapper) { min-height: 42px; border-radius: 9px; }
.points-preview { display: flex; align-items: baseline; flex-wrap: wrap; gap: 10px 12px; margin: 20px 0 26px; color: var(--muted); font-size: 13px; }
.points-preview strong { color: var(--ink); font-size: 20px; font-weight: 560; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.points-preview small { font-size: 13px; font-weight: 400; }
.unit-price { margin-left: 4px; color: var(--muted); font-size: 12px; }
.payment-methods :deep(.el-radio-group) { display: flex; gap: 12px; flex-wrap: wrap; }
.payment-methods :deep(.el-radio) { position: relative; height: 46px; margin: 0; padding: 0 18px; border-radius: 9px; }
.payment-methods :deep(.el-radio__input) { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; }
.payment-methods :deep(.el-radio__inner) { display: none; }
.payment-methods :deep(.el-radio__original) { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; }
.payment-methods :deep(.el-radio:focus-within) { outline: none; }
.payment-methods :deep(.el-radio__original:focus-visible) { outline: none; }
.payment-methods :deep(.el-radio:has(.el-radio__original:focus-visible)) { border-color: var(--brand); box-shadow: none; }
.payment-methods :deep(.el-radio__label) { display: flex; align-items: center; gap: 8px; padding: 0; }
.payment-methods :deep(.is-checked) { background: var(--brand-soft); border-color: var(--brand); }
.pay-brand { display: inline-grid; place-items: center; width: 22px; height: 22px; }
.alipay { border-radius: 5px; background: #1677ff; color: white; font-size: 13px; font-weight: 700; }
.wechat { color: #16b66d; }
.wechat svg { width: 24px; height: 24px; }
.purchase-submit { display: flex; align-items: center; flex-wrap: wrap; gap: 20px 28px; margin-top: 30px; }
.purchase-amount { display: flex; align-items: baseline; flex-wrap: wrap; gap: 12px; }
.purchase-amount > span { color: var(--text); font-size: 13px; }
.purchase-amount > strong { color: var(--ink); font-size: 28px; line-height: 1.25; font-weight: 620; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.purchase-amount > strong small { margin-right: 3px; font-size: 19px; font-weight: 500; }
.purchase-submit :deep(.el-button--large) { min-width: 168px; height: 44px; margin: 0; border-radius: 9px; font-size: 14px; }
.pay-arrow { margin-left: 10px; }
.amount-error, .payment-error { color: var(--danger); line-height: 1.6; font-size: 13px; }
.payment-error { margin: 0 0 16px; }
.payment-inline-state { max-width: 960px; margin-top: 20px; }
.checkout-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 6px; }
.checkout-panel-head h2 { margin: 0; color: var(--ink); font-size: 18px; font-weight: 620; }
.checkout-panel-head p { margin: 7px 0 0; color: var(--muted); font-size: 13px; }
.return-alert { margin-bottom: 16px; }
.return-notice { margin: -6px 0 18px; padding: 10px 12px; border-radius: 8px; background: var(--brand-soft); color: var(--brand-deep); font-size: 13px; line-height: 1.6; }
:global(.checkout-dialog) { max-width: calc(100vw - 32px); border-radius: 16px; }
:global(.checkout-dialog .el-dialog__header) { margin-right: 0; padding-bottom: 6px; }
:global(.checkout-dialog .el-dialog__body) { padding-top: 8px; }
.loading-state { display: flex; align-items: center; gap: 10px; min-height: 90px; color: var(--muted); font-size: 13px; }
.spin { animation: spin 1s linear infinite; }
.checkout-dialog-content { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 8px 0 14px; text-align: center; }
.checkout-dialog-content > p, .checkout-dialog-content h3 { margin: 0; }
.checkout-dialog-content > p { color: var(--muted); font-size: 13px; line-height: 1.7; }
.checkout-dialog-content .payment-error { color: var(--danger); }
.checkout-actions { display: flex; align-items: center; flex-wrap: wrap; justify-content: center; gap: 16px; }
.checkout-actions :deep(.el-button) { margin-left: 0; }
.checkout-amount { color: var(--ink); font-size: 32px; font-weight: 650; font-variant-numeric: tabular-nums; }
.qr-frame { position: relative; width: min(280px, 100%); overflow: hidden; border: 1px solid var(--line-light); border-radius: 12px; background: white; }
.qr-frame img { display: block; width: 100%; height: auto; aspect-ratio: 1; }
.qr-frame.expired img { filter: blur(5px); opacity: .15; }
.qr-expired { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px; background: rgba(255,255,255,.9); }
.checkout-polling { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 5px; font-size: 12px !important; }
.status-dot { display: inline-block; width: 6px; height: 6px; margin-right: 3px; border-radius: 50%; background: var(--success); }
.usage-panel { margin-top: 20px; }
.panel-head { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; min-height: 160px; color: var(--muted); font-size: 13px; }
.empty-state strong { color: var(--text); font-size: 14px; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 1100px) {
  .summary-grid { grid-template-columns: 1fr; }
  .amount-presets { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 640px) {
  .recharge-panel { padding: 20px 18px 16px; }
  .custom-amount { max-width: none; }
  .amount-presets { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .purchase-submit { align-items: stretch; flex-direction: column; gap: 16px; }
  .purchase-submit :deep(.el-button--large) { width: 100%; }
  .unit-price { flex-basis: 100%; margin-left: 0; }
}
</style>
